import ReactModal from 'react-modal';
import General from '../utils/GeneralUtils';
import { EmailFormatTag } from './EmailFormatTag';
import './EmailVerificationSlideout.scss';
import './InstitutionEmailFormatsSlideout.scss';

/** One observed email pattern on a domain. */
export interface InstitutionEmailFormat {
  emailFormat: string;
  contactCount: number;
  percentage: number;
  rank: number;
}

/** A domain of the institution and the patterns seen on it. */
export interface InstitutionDomainEmailFormats {
  domain: string;
  domainTotal: number;
  formats: InstitutionEmailFormat[];
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  institutionName?: string;
  emailFormats: InstitutionDomainEmailFormats[];
}

/**
 * The full email-pattern breakdown for an institution: every domain the API returned, and
 * every pattern on it. The top card shows only the leading pattern of the largest domain,
 * so this is where the alternates and the secondary domains live.
 */
export function InstitutionEmailFormatsSlideout({ isOpen, onClose, institutionName, emailFormats }: Props) {
  return (
    <ReactModal
      isOpen={isOpen}
      shouldCloseOnEsc={true}
      shouldCloseOnOverlayClick={true}
      onRequestClose={onClose}
      style={General.getSliderDefaults({ width: 'min(90vw, 500px)' })}
      parentSelector={General.getModalRoot}
    >
      <div className="email-verification-slideout institution-email-formats">
        <div className="header">
          <h3>Email Formats</h3>
        </div>

        <div className="section intro">
          <p>
            How email addresses are built from names at {institutionName || 'this institution'},
            measured across contacts we have already observed. Percentages are of the contacts
            on that domain whose address matched a known pattern.
          </p>
        </div>

        {emailFormats.map((it) => (
          <div className="section" key={it.domain}>
            <h4>
              {it.domain}
              <span className="institution-email-formats__total">
                {it.domainTotal.toLocaleString()} contact{it.domainTotal === 1 ? '' : 's'}
              </span>
            </h4>
            <table>
              <tbody>
                {it.formats.map((f) => (
                  <tr key={f.emailFormat} className="institution-email-formats__row">
                    <td className="institution-email-formats__tag-cell">
                      <EmailFormatTag
                        format={f.emailFormat}
                        domain={it.domain}
                        percentage={f.percentage}
                        contactCount={f.contactCount}
                        domainTotal={it.domainTotal}
                        size="small"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </ReactModal>
  );
}
